import React, { useEffect, useMemo } from "react";
import { Stack, Box, InputAdornment } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createFeeStructure,
  updateFeeStructure,
} from "../redux/actions/feeStructureActions";
import { getBranches } from "../redux/actions/branchActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { getTerms } from "../redux/actions/termActions";
import { getGradeLevels } from "../redux/actions/gradeLevelActions";
import { getVoteHeads } from "../redux/actions/voteHeadActions";
import { feeStructureActions } from "../redux/slices/feeStructure";
import { cleanFormData } from "../helpers/formHelpers";

import { FeeStructureResponseDto } from "../shared/dtos/feeStructure/FeeStructureResponseDto";
import { FeeStructureRequestDto } from "../shared/dtos/feeStructure/FeeStructureRequestDto";

const feeStructureSchema = Yup.object().shape({
  branchId: Yup.string().required("Branch is required"),
  academicYearId: Yup.string().required("Academic Year is required"),
  termId: Yup.string().required("Term is required"),
  gradeLevelId: Yup.string().required("Grade Level is required"),
  voteHeadId: Yup.string().required("Vote Head is required"),
  amount: Yup.number()
    .required("Amount is required")
    .min(0, "Amount cannot be negative"),
});

interface CreateOrUpdateFeeStructureFormProps {
  feeStructure?: FeeStructureResponseDto | null;
  userInfo?: any;
  isEditing?: boolean;
  loading?: boolean;
  error?: string | null;
}

const CreateOrUpdateFeeStructureForm: React.FC<
  CreateOrUpdateFeeStructureFormProps
> = ({
  feeStructure = null,
  userInfo = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: branches } = useAppSelector((state) => state.branch);
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { items: terms } = useAppSelector((state) => state.term);
  const { items: gradeLevels } = useAppSelector((state) => state.gradeLevel);
  const { items: voteHeads } = useAppSelector((state) => state.voteHead);

  const isSuperAdmin = userInfo?.userRole === "SUPER_ADMIN";

  useEffect(() => {
    if (isSuperAdmin && (!branches || branches.length === 0))
      dispatch(getBranches());
    if (!academicYears || academicYears.length === 0)
      dispatch(getAcademicYears());
    if (!terms || terms.length === 0) dispatch(getTerms());
    if (!gradeLevels || gradeLevels.length === 0) dispatch(getGradeLevels());
    if (!voteHeads || voteHeads.length === 0) dispatch(getVoteHeads());
  }, [
    dispatch,
    branches,
    academicYears,
    terms,
    gradeLevels,
    voteHeads,
    isSuperAdmin,
  ]);

  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.id, label: b.name })) || [],
    [branches],
  );
  const yearOptions = useMemo(
    () => academicYears?.map((y) => ({ value: y.id, label: y.name })) || [],
    [academicYears],
  );
  const termOptions = useMemo(
    () => terms?.map((t) => ({ value: t.id, label: t.name })) || [],
    [terms],
  );
  const gradeOptions = useMemo(
    () => gradeLevels?.map((g) => ({ value: g.id, label: g.name })) || [],
    [gradeLevels],
  );
  const voteHeadOptions = useMemo(
    () => voteHeads?.map((v) => ({ value: v.id, label: v.name })) || [],
    [voteHeads],
  );

  const initialValues: FeeStructureRequestDto = {
    branchId:
      feeStructure?.branchId || (isSuperAdmin ? "" : userInfo?.branchId) || "",
    academicYearId: feeStructure?.academicYearId || "",
    termId: feeStructure?.termId || "",
    gradeLevelId: feeStructure?.gradeLevelId || "",
    voteHeadId: feeStructure?.voteHeadId || "",
    amount: feeStructure?.amount || 0,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(feeStructureActions.setErrorUpdate(null))
            : () => dispatch(feeStructureActions.setErrorCreate(null))
        }
      />

      <Formik
        initialValues={initialValues}
        validationSchema={feeStructureSchema}
        onSubmit={(values) => {
          const rawCleaned = cleanFormData(values) as Record<string, any>;
          const cleanedValues: FeeStructureRequestDto = {
            branchId: rawCleaned.branchId,
            academicYearId: rawCleaned.academicYearId,
            termId: rawCleaned.termId,
            gradeLevelId: rawCleaned.gradeLevelId,
            voteHeadId: rawCleaned.voteHeadId,
            amount: Number(rawCleaned.amount),
          };

          if (isEditing && feeStructure) {
            dispatch(
              updateFeeStructure({
                id: feeStructure.id,
                values: cleanedValues,
              }),
            );
          } else {
            dispatch(createFeeStructure(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<FeeStructureRequestDto>) => (
          <Form>
            <Stack spacing={2}>
              {isSuperAdmin && (
                <FormSelectField
                  name="branchId"
                  label="Branch"
                  options={branchOptions}
                />
              )}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="academicYearId"
                  label="Academic Year"
                  options={yearOptions}
                />
                <FormSelectField
                  name="termId"
                  label="Term"
                  options={termOptions}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormSelectField
                  name="gradeLevelId"
                  label="Grade Level"
                  options={gradeOptions}
                />
                <FormSelectField
                  name="voteHeadId"
                  label="Vote Head"
                  options={voteHeadOptions}
                />
              </Box>

              <FormTextField
                name="amount"
                label="Amount"
                type="number"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">KES</InputAdornment>
                    ),
                  },
                  htmlInput: { min: 0 },
                }}
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Fee Structure"}
                loadingText={isEditing ? "Saving..." : "Creating..."}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  );
};

export default CreateOrUpdateFeeStructureForm;
